// main.js - Main application initialization

// Import store module
import store from "./modules/store.js";
import { loadTheme } from "./modules/theme.js";
import {
  setupUI,
  isMobileDevice,
  hasAcceptedDisclaimer,
  showDisclaimerModal,
  setupMobileUI,
  addQrCodeButton,
} from "./modules/ui.js";
import { setupAPISettings, apiUrl } from "./modules/api-settings.js";
import { setupAuthentication } from "./modules/authentication.js";
import {
  setupConversations,
  loadConversations,
  createNewConversation,
} from "./modules/conversations.js";
import { setupMessageHandling, setupSuggestions } from "./modules/messages.js";
import { migrateStorageData } from "./modules/utils.js";
import { setupEventListeners } from "./modules/event-listeners.js";
// Import featureSystem
import { featureSystem } from './features.js';

/**
 * Set up the disclaimer events to ensure proper handling
 */
function setupDisclaimerEvents() {
  const disclaimerModal = document.getElementById("disclaimerModal");
  const disclaimerCheckbox = document.getElementById("disclaimerCheckbox");
  const disclaimerAgreeBtn = document.getElementById("disclaimerAgreeBtn");
  const appContainer = document.getElementById("appContainer");

  if (disclaimerAgreeBtn && disclaimerCheckbox && disclaimerModal) {
    // Supprime les événements précédents
    const newBtn = disclaimerAgreeBtn.cloneNode(true);
    disclaimerAgreeBtn.parentNode.replaceChild(newBtn, disclaimerAgreeBtn);

    // Ajout du nouvel événement
    newBtn.addEventListener("click", function () {
      if (disclaimerCheckbox.checked) {
        localStorage.setItem("quantumDisclaimerAccepted", "true");
        disclaimerModal.style.display = "none";
        if (appContainer) appContainer.style.display = "flex";
      }
    });

    // Vérifie l'état de la checkbox pour activer/désactiver le bouton
    disclaimerCheckbox.addEventListener("change", function () {
      newBtn.disabled = !this.checked;
    });
  }
}

/**
 * Ensure all modals are properly hidden when showing the app
 */
function hideAllModals() {
  const modals = [
    "loginModal",
    "disclaimerModal",
    "learnMoreModal",
    "walletModal",
    "policyModal",
    "limitReachedModal",
    "logoutModal",
    "confirmModal",
    "reportModal"
  ];

  modals.forEach((modalId) => {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.style.display = "none";
    }
  });
}

async function checkForLoginToken() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const loginToken = urlParams.get("loginToken");
    const walletAddress = urlParams.get("walletAddress");

    if (loginToken && walletAddress) {
      // Nettoyer l'URL pour des raisons de sécurité
      window.history.replaceState({}, document.title, window.location.pathname);

      // Montrer une notification de chargement
      const loadingNotification = showNotification(
        "Authentication in progress...",
        "info"
      );

      const response = await fetch(`${apiUrl}/verify-mobile-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: loginToken,
          deviceId: getDeviceId(),
        }),
      });

      if (!response.ok) {
        throw new Error(
          (await response.text()) || "Token verification failed"
        );
      }

      const data = await response.json();

      // Stocker les informations d'authentification
      localStorage.setItem("quantumAuthToken", data.token);
      
      // Store user data including account type
      const userData = {
        walletAddress: walletAddress,
        accountType: walletAddress.includes('@') ? 'google' : 'wallet',
        credits: data.credits || 0,
        email: walletAddress.includes('@') ? walletAddress : null,
        lastActive: new Date().toISOString()
      };
      
      localStorage.setItem("quantumUserData", JSON.stringify(userData));
      
      // Update store
      store.setAuthenticated(true);
      store.setGuestMode(false);
      store.setWalletAddress(walletAddress);
      store.setAccountType(userData.accountType);
      store.setUserEmail(userData.email);
      store.setUserCredits(userData.credits);

      onSuccessfulAuth();

      showNotification("Successfully connected!", "success");
    }
  } catch (error) {
    showNotification(`Authentication error: ${error.message}`, "error");
  }
}

/**
 * Initialize the application
 */
async function init() {
  try {

    migrateStorageData();
    // Load theme from localStorage
    loadTheme();

    // Set up user interface
    setupUI();

    // Setup disclaimer events
    setupDisclaimerEvents();

    // Set up mobile-specific UI if needed
    if (isMobileDevice()) {
      setupMobileUI();
    }

    // Set up API settings
    setupAPISettings();

    // Set up conversations management
    setupConversations();

    // Set up message handling
    setupMessageHandling();

    // Set up authentication
    setupAuthentication();

    // Set up event listeners (do this last so all modules are initialized)
    setupEventListeners();

    // Set up suggestions component
    setupSuggestions();

    // Initialize the feature system AFTER store and conversations are ready
    // Pass the imported store instance and createNewConversation function
    featureSystem.initialize(store, createNewConversation);

    // Check for existing auth token
    if (isMobileDevice()) {
      const authToken = localStorage.getItem("quantumAuthToken");
      if (authToken) {
        try {
          const response = await fetch(`${apiUrl}/verify-jwt-mobile-token`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            console.log("User authenticated with existing token:", data);

            // Get saved user data if available
            const savedUserData = localStorage.getItem('quantumUserData');
            if (savedUserData) {
              try {
                const userData = JSON.parse(savedUserData);
                store.setAuthenticated(true);
                store.setGuestMode(false);
                store.setWalletAddress(userData.walletAddress || null);
                store.setAccountType(userData.accountType || (userData.walletAddress?.includes('@') ? 'google' : 'wallet'));
                store.setUserEmail(userData.email || (userData.accountType === 'google' ? userData.walletAddress : null));
                store.setUserCredits(userData.credits || 0);
              } catch (err) {
                console.error("Error parsing saved user data:", err);
                // Fallback to basic data from response
                store.setAuthenticated(true);
                store.setGuestMode(false);
                store.setWalletAddress(data.walletAddress || null);
              }
            } else {
              // No saved data, use response data
              store.setAuthenticated(true);
              store.setGuestMode(false);
              store.setWalletAddress(data.walletAddress || null);
              // Infer account type if possible
              if (data.walletAddress && data.walletAddress.includes('@')) {
                store.setAccountType('google');
                store.setUserEmail(data.walletAddress);
              } else {
                store.setAccountType('wallet');
              }
            }

            // Handle successful authentication
            onSuccessfulAuth();
            return;
          } else {
            console.warn("Token verification failed, clearing stored token");
            localStorage.removeItem("quantumAuthToken");
            localStorage.removeItem("quantumUserData");
          }
        } catch (error) {
          console.error("Error verifying existing token:", error);
        }
      }
    }
    checkForLoginToken();
  } catch (error) {
    console.error("Error during initialization:", error);
  }
}

/**
 * Handle successful authentication
 */
function onSuccessfulAuth() {
  console.log("Authentication successful, setting up UI");

  hideAllModals();

  const appContainer = document.getElementById("appContainer");
  if (appContainer) {
    appContainer.style.display = "flex";
    console.log("App container shown");
  }

  // Charge les conversations après que l'UI soit affichée
  loadConversations();
  console.log("Conversations loaded");

  // Vérifie si l'utilisateur a accepté le disclaimer
  const disclaimerAccepted =
    localStorage.getItem("quantumDisclaimerAccepted") ||
    localStorage.getItem("alaricDisclaimerAccepted");

  console.log("Disclaimer accepted?", disclaimerAccepted);

  if (disclaimerAccepted === "true") {
    // L'utilisateur a déjà accepté, assurez-vous de sauvegarder avec la nouvelle clé
    localStorage.setItem("quantumDisclaimerAccepted", "true");
    console.log("Disclaimer already accepted");
  } else {
    // N'a pas encore accepté le disclaimer
    console.log("Showing disclaimer modal");
    const disclaimerModal = document.getElementById("disclaimerModal");
    if (disclaimerModal) {
      disclaimerModal.style.display = "flex";
    }
  }

  // Crée une nouvelle conversation si aucune n'existe
  const conversationsList = document.getElementById("conversationsList");
  if (conversationsList && conversationsList.children.length === 0) {
    console.log("Creating new conversation");
    createNewConversation();
  }

  if (!isMobileDevice()) {
    addQrCodeButton();
  }
}

// Initialize the application when the DOM is loaded
document.addEventListener("DOMContentLoaded", init);

// Export necessary functions
export { onSuccessfulAuth, hideAllModals };